import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  LocationCity as CityIcon,
  Map as StateIcon,
  MarkunreadMailbox as ZipIcon,
  Public as CountryIcon,
} from '@mui/icons-material';
import { Grid, TextField, InputAdornment, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { validatePhoneNumber } from '../../../utils/phoneUtils';
import InternationalPhoneField from '../../FormFields/InternationalPhoneField';

/**
 * ContactFormFields - Reusable contact form fields for Formik forms
 *
 * Provides standardized contact/address field group that can be used across:
 * - ContactForm (unified contact form component)
 * - RMA forms (IssueRmaPage, ProcessRmaPage, QuickShipRmaPage)
 * - Order forms
 * - Any form that needs contact/address information
 *
 * Primary context options:
 * - 'all': Show both RMA and Sales primary options (Contact Management)
 * - 'rma': Show only RMA primary options (RMA forms)
 * - 'sales': Show only Sales primary options (Order forms)
 * - 'none': Hide all primary options (Address-only mode)
 */
const ContactFormFields = ({
  fieldPrefix = '',
  primaryContext = 'none',
  showBusinessName = true,
  requiredAddress = false,
  requiredContact = false,
  requiredFields = [],
  gridColumns = { xs: 12, sm: 6 },
  addressOnlyMode = false, // When true, hide contact info fields (name, email, phone) and only show address fields
  validateIfAnyFilled = false, // When true, show required asterisks if ANY field has a value
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext();

  /**
   * Helper to generate field names with optional prefix
   * @param {string} name - Base field name
   * @returns {string} Prefixed field name (e.g., "ship_contact")
   */
  const fieldName = name => `${fieldPrefix}${name}`;

  // Determine if address is required based on any primary checkbox
  const isPrimaryRmaBilling = formik?.values?.[fieldName('is_primary_rma_billing')] || false;
  const isPrimaryRmaShipping = formik?.values?.[fieldName('is_primary_rma_shipping')] || false;
  const isPrimarySalesBilling = formik?.values?.[fieldName('is_primary_sales_billing')] || false;
  const isPrimarySalesShipping = formik?.values?.[fieldName('is_primary_sales_shipping')] || false;

  // Check if any address field has a value (for partial address validation)
  const hasAnyAddressField =
    !!formik?.values?.[fieldName('address')]?.trim() ||
    !!formik?.values?.[fieldName('city')]?.trim() ||
    !!formik?.values?.[fieldName('state')]?.trim() ||
    !!formik?.values?.[fieldName('zipcode')]?.trim();

  // Check if ANY contact field has a value (for validateIfAnyFilled mode)
  const hasAnyContactField =
    !!formik?.values?.[fieldName('name')]?.trim() ||
    !!formik?.values?.[fieldName('business_name')]?.trim() ||
    !!formik?.values?.[fieldName('email')]?.trim() ||
    !!formik?.values?.[fieldName('phone1')]?.trim() ||
    !!formik?.values?.[fieldName('address')]?.trim() ||
    !!formik?.values?.[fieldName('address_2')]?.trim() ||
    !!formik?.values?.[fieldName('city')]?.trim() ||
    !!formik?.values?.[fieldName('state')]?.trim() ||
    !!formik?.values?.[fieldName('zipcode')]?.trim() ||
    !!formik?.values?.[fieldName('country')]?.trim();

  // When validateIfAnyFilled is true and any field has a value, name/email/address become required
  const isConditionallyRequired = validateIfAnyFilled && hasAnyContactField;

  // Address is required if: explicitly required, any primary checkbox checked, any address field is filled, OR conditionally required
  const isAddressRequired =
    requiredAddress ||
    isPrimaryRmaBilling ||
    isPrimaryRmaShipping ||
    isPrimarySalesBilling ||
    isPrimarySalesShipping ||
    hasAnyAddressField ||
    isConditionallyRequired;

  // Determine which primary sections to show
  const showRmaPrimary = primaryContext === 'all' || primaryContext === 'rma';
  const showSalesPrimary = primaryContext === 'all' || primaryContext === 'sales';

  // Handle checkbox changes for primary options
  const handleCheckboxChange = fieldKey => event => {
    formik.setFieldValue(fieldName(fieldKey), event.target.checked);
  };

  return (
    <>
      {/* ===== CONTACT INFORMATION SECTION ===== */}
      {!addressOnlyMode && (
        <>
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -1 }}>
              {t('contacts.sections.contactInfo', 'Contact Information')}
            </Typography>
          </Grid>

          {/* Name Field */}
          <Grid size={{ xs: 12, sm: gridColumns.sm }}>
            <Field name={fieldName('name')}>
              {({ field, meta }) => (
                <TextField
                  {...field}
                  fullWidth
                  required={isConditionallyRequired || requiredFields.includes('name')}
                  label={t('contacts.management.formNameLabel', 'Name')}
                  error={meta.touched && !!meta.error}
                  helperText={meta.touched && meta.error}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Field>
          </Grid>

          {/* Business Name Field (Optional) */}
          {showBusinessName && (
            <Grid size={{ xs: 12, sm: gridColumns.sm }}>
              <Field name={fieldName('business_name')}>
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('contacts.management.formBusinessLabel', 'Business Name')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Field>
            </Grid>
          )}

          {/* Email Field - Required only when explicitly required, primary is checked, or conditionally required */}
          <Grid size={{ xs: 12, sm: gridColumns.sm }}>
            <Field name={fieldName('email')}>
              {({ field, meta }) => {
                const isEmailExplicitlyRequired = requiredFields.includes('email');
                const isEmailOrPhoneRequired = requiredFields.includes('emailOrPhone');
                const isPrimary =
                  isPrimaryRmaBilling || isPrimaryRmaShipping || isPrimarySalesBilling || isPrimarySalesShipping;
                // Show required indicator only if:
                // - Email is explicitly required, OR
                // - A primary checkbox is checked (primary contacts always need email), OR
                // - validateIfAnyFilled is true and any field has a value
                // Note: 'emailOrPhone' means either is acceptable, so neither shows as "required"
                const showRequired =
                  isEmailExplicitlyRequired || (isPrimary && isEmailOrPhoneRequired) || isConditionallyRequired;
                return (
                  <TextField
                    {...field}
                    fullWidth
                    type="email"
                    required={showRequired}
                    label={t('contacts.management.formEmailLabel', 'Email')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              }}
            </Field>
          </Grid>

          {/* Phone Field - International support with country selector */}
          <Grid size={{ xs: 12, sm: gridColumns.sm }}>
            <Field name={fieldName('phone1')}>
              {({ field, meta, form }) => (
                <InternationalPhoneField
                  name={field.name}
                  value={field.value}
                  onChange={value => form.setFieldValue(field.name, value)}
                  onBlur={field.onBlur}
                  label={t('contacts.management.formPhoneLabel', 'Phone')}
                  error={meta.touched && !!meta.error}
                  helperText={meta.touched && meta.error}
                  showValidation={false}
                />
              )}
            </Field>
          </Grid>
        </>
      )}

      {/* ===== ADDRESS SECTION ===== */}
      {/* Hide address label in addressOnlyMode since it's the only section shown */}
      {!addressOnlyMode && (
        <Grid size={12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: -1 }}>
            {t('contacts.sections.address', 'Address')}
          </Typography>
        </Grid>
      )}

      {/* Street Address Field */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Field name={fieldName('address')}>
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              required={isAddressRequired}
              label={t('contacts.management.formAddressLabel', 'Street Address')}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Field>
      </Grid>

      {/* Address Line 2 Field (Apt/Suite) */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Field name={fieldName('address_2')}>
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              label={t('contacts.management.formAddress2Label', 'Apt / Suite / Unit')}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Field>
      </Grid>

      {/* City Field */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Field name={fieldName('city')}>
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              required={isAddressRequired}
              label={t('contacts.management.formCityLabel', 'City')}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CityIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Field>
      </Grid>

      {/* State Field */}
      <Grid size={{ xs: 12, sm: 3 }}>
        <Field name={fieldName('state')}>
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              required={isAddressRequired}
              label={t('contacts.management.formStateLabel', 'State')}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StateIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Field>
      </Grid>

      {/* Postal Code Field */}
      <Grid size={{ xs: 12, sm: 3 }}>
        <Field name={fieldName('zipcode')}>
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              required={isAddressRequired}
              label={t('contacts.management.formZipLabel', 'Postal Code')}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ZipIcon color="action" />
                  </InputAdornment>
                ),
              }}
              onChange={e => {
                // Allow alphanumeric characters, spaces, and hyphens (for international postal codes like Canada)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s-]/g, '').toUpperCase();
                field.onChange({ target: { name: field.name, value } });
              }}
            />
          )}
        </Field>
      </Grid>

      {/* Country Field */}
      <Grid size={12}>
        <Field name={fieldName('country')}>
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              required={isAddressRequired}
              label={t('contacts.management.formCountryLabel', 'Country')}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CountryIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Field>
      </Grid>

      {/* ===== RMA PRIMARY SECTION ===== */}
      {showRmaPrimary && (
        <>
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0 }}>
              {t('contacts.sections.rmaPrimary', 'RMA Primary')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(
                'contacts.sections.rmaPrimaryHint',
                'Default contact for RMA billing/shipping. Requires complete address.',
              )}
            </Typography>
          </Grid>
          <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formik?.values?.[fieldName('is_primary_rma_billing')]}
                  onChange={handleCheckboxChange('is_primary_rma_billing')}
                  color="primary"
                />
              }
              label={t('contacts.primary.billing', 'Billing')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formik?.values?.[fieldName('is_primary_rma_shipping')]}
                  onChange={handleCheckboxChange('is_primary_rma_shipping')}
                  color="primary"
                />
              }
              label={t('contacts.primary.shipping', 'Shipping')}
            />
          </Grid>
        </>
      )}

      {/* ===== SALES PRIMARY SECTION ===== */}
      {showSalesPrimary && (
        <>
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0 }}>
              {t('contacts.sections.salesPrimary', 'Sales Primary')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(
                'contacts.sections.salesPrimaryHint',
                'Default contact for sales orders billing/shipping. Requires complete address.',
              )}
            </Typography>
          </Grid>
          <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formik?.values?.[fieldName('is_primary_sales_billing')]}
                  onChange={handleCheckboxChange('is_primary_sales_billing')}
                  color="primary"
                />
              }
              label={t('contacts.primary.billing', 'Billing')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formik?.values?.[fieldName('is_primary_sales_shipping')]}
                  onChange={handleCheckboxChange('is_primary_sales_shipping')}
                  color="primary"
                />
              }
              label={t('contacts.primary.shipping', 'Shipping')}
            />
          </Grid>
        </>
      )}
    </>
  );
};

ContactFormFields.propTypes = {
  /** Prefix for field names (e.g., "ship_" creates "ship_contact", "ship_email") */
  fieldPrefix: PropTypes.string,
  /** Context for primary options: 'all', 'rma', 'sales', or 'none' */
  primaryContext: PropTypes.oneOf(['all', 'rma', 'sales', 'none']),
  /** Whether to show business_name field */
  showBusinessName: PropTypes.bool,
  /** Whether address fields (address, city, state, zipcode) are required */
  requiredAddress: PropTypes.bool,
  /** @deprecated No longer used */
  requiredContact: PropTypes.bool,
  /** Array of required fields: 'email', 'name', 'address', 'phone' */
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  /** Responsive column sizing for Grid items */
  gridColumns: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
  }),
  /** When true, hide contact info fields (name, email, phone) and only show address fields */
  addressOnlyMode: PropTypes.bool,
  /** When true, show required asterisks if ANY field has a value */
  validateIfAnyFilled: PropTypes.bool,
};

ContactFormFields.getValidationSchema = (t, options = {}) => {
  const {
    fieldPrefix = '',
    requiredAddress = false,
    showBusinessName = true,
    primaryContext = 'none',
    requiredName = true,
    requiredContact = false,
    requiredFields = [],
    addressOnlyMode = false, // When true, skip name/contact validation (fields are hidden)
    noRequiredFields = false, // When true, no fields are required - all validation is optional
  } = options;

  // Derive individual requirements from requiredFields array
  const requireEmailOnly = requiredFields.includes('email');
  const requireAddressFromFields = requiredFields.includes('address');
  const requireNameFromFields = requiredFields.includes('name');
  const requirePhoneFromFields = requiredFields.includes('phone');
  const requireEmailOrPhone = requiredFields.includes('emailOrPhone');

  const fieldName = name => `${fieldPrefix}${name}`;

  // Base schema shape
  const schemaShape = {};

  // In addressOnlyMode or noRequiredFields mode, don't require name
  // Combine requiredName with requireNameFromFields
  const isNameRequired = noRequiredFields || addressOnlyMode ? false : requiredName || requireNameFromFields;

  // Name field - conditionally required based on requiredName option or requiredFields
  if (isNameRequired) {
    schemaShape[fieldName('name')] = Yup.string()
      .max(255, t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }))
      .test(
        'name-or-business-required',
        t('validation.nameOrBusinessRequired', 'Either Name or Business Name is required'),
        function (value) {
          const businessName = this.parent[fieldName('business_name')];
          // At least one of name or business_name must be filled
          return !!(value && value.trim()) || !!(businessName && businessName.trim());
        },
      );
  } else {
    schemaShape[fieldName('name')] = Yup.string().max(
      255,
      t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }),
    );
  }

  // Email field validation:
  // - If noRequiredFields: email is optional
  // - If requiredFields includes 'email' (e.g., Send Confirmation checked): always required
  // - If any primary flag is set: email required (handled by cross-field validation below)
  // - Otherwise: email OR phone required (handled by cross-field validation below)
  const requireEmailExplicitly = requiredFields.includes('email');
  if (noRequiredFields || addressOnlyMode) {
    // noRequiredFields or addressOnlyMode - email is optional
    schemaShape[fieldName('email')] = Yup.string()
      .email(t('validation.email', 'Invalid email address'))
      .max(255, t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }));
  } else if (requireEmailExplicitly) {
    schemaShape[fieldName('email')] = Yup.string()
      .email(t('validation.email', 'Invalid email address'))
      .max(255, t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }))
      .required(t('validation.emailRequired', 'Email is required'));
  } else if (!addressOnlyMode && requireEmailOrPhone) {
    // Cross-field validation: primary contacts need email, others need email OR phone
    schemaShape[fieldName('email')] = Yup.string()
      .email(t('validation.email', 'Invalid email address'))
      .max(255, t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }))
      .test('email-required-for-primary-or-contact', function (value) {
        const { is_primary_rma_billing, is_primary_rma_shipping, is_primary_sales_billing, is_primary_sales_shipping } =
          this.parent;
        const isPrimary =
          is_primary_rma_billing || is_primary_rma_shipping || is_primary_sales_billing || is_primary_sales_shipping;
        const phone = this.parent[fieldName('phone1')];

        if (isPrimary) {
          // Primary contacts require email
          if (!value || !value.trim()) {
            return this.createError({
              message: t('validation.emailRequiredForPrimary', 'Primary contacts must have an email address'),
            });
          }
        } else {
          // Non-primary contacts require email OR phone
          if ((!value || !value.trim()) && (!phone || !phone.trim())) {
            return this.createError({
              message: t('validation.emailOrPhoneRequired', 'Either email or phone is required'),
            });
          }
        }
        return true;
      });
  } else {
    // Email is optional - no email/phone requirement (e.g., RMA shipping/billing)
    schemaShape[fieldName('email')] = Yup.string()
      .email(t('validation.email', 'Invalid email address'))
      .max(255, t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }));
  }

  // Phone field validation helper - validates international phone numbers
  const phoneValidation = Yup.string().test(
    'valid-phone',
    t('validation.invalidPhone', 'Invalid phone number'),
    value => {
      if (!value || !value.trim()) return true; // Empty is valid (use required for mandatory)
      return validatePhoneNumber(value);
    },
  );

  // Phone field - conditionally required based on requiredFields or as alternative to email
  if (noRequiredFields || addressOnlyMode) {
    // noRequiredFields or addressOnlyMode - phone is optional
    schemaShape[fieldName('phone1')] = phoneValidation;
  } else if (requirePhoneFromFields) {
    // Phone is specifically required
    schemaShape[fieldName('phone1')] = phoneValidation.required(t('validation.phoneRequired', 'Phone is required'));
  } else if (!addressOnlyMode && requireEmailOrPhone) {
    // Cross-field validation: non-primary contacts can use phone instead of email
    schemaShape[fieldName('phone1')] = phoneValidation.test('phone-or-email-required', function (value) {
      const { is_primary_rma_billing, is_primary_rma_shipping, is_primary_sales_billing, is_primary_sales_shipping } =
        this.parent;
      const isPrimary =
        is_primary_rma_billing || is_primary_rma_shipping || is_primary_sales_billing || is_primary_sales_shipping;

      // Primary contacts don't need phone validation (they need email)
      if (isPrimary) return true;

      const email = this.parent[fieldName('email')];
      // Non-primary contacts require email OR phone
      if ((!value || !value.trim()) && (!email || !email.trim())) {
        return this.createError({
          message: t('validation.emailOrPhoneRequired', 'Either email or phone is required'),
        });
      }
      return true;
    });
  } else {
    // Phone is optional (e.g., RMA shipping/billing, or when email is explicitly required)
    schemaShape[fieldName('phone1')] = phoneValidation;
  }

  // Add business_name if shown
  if (showBusinessName) {
    if (isNameRequired) {
      schemaShape[fieldName('business_name')] = Yup.string()
        .max(255, t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }))
        .test(
          'business-or-name-required',
          t('validation.nameOrBusinessRequired', 'Either Name or Business Name is required'),
          function (value) {
            const name = this.parent[fieldName('name')];
            // At least one of name or business_name must be filled
            return !!(value && value.trim()) || !!(name && name.trim());
          },
        );
    } else {
      schemaShape[fieldName('business_name')] = Yup.string().max(
        255,
        t('validation.maxLength', 'Maximum {{max}} characters', { max: 255 }),
      );
    }
  }

  // Combine requiredAddress with requireAddressFromFields
  const isAddressRequired = requiredAddress || requireAddressFromFields;

  // Add address fields
  if (isAddressRequired) {
    schemaShape[fieldName('address')] = Yup.string()
      .required(t('validation.required', 'This field is required'))
      .max(500, t('validation.maxLength', 'Maximum {{max}} characters', { max: 500 }));

    schemaShape[fieldName('address_2')] = Yup.string().max(
      100,
      t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }),
    );

    schemaShape[fieldName('city')] = Yup.string()
      .required(t('validation.required', 'This field is required'))
      .max(100, t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }));

    schemaShape[fieldName('state')] = Yup.string()
      .required(t('validation.required', 'This field is required'))
      .max(100, t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }));

    schemaShape[fieldName('zipcode')] = Yup.string()
      .required(t('validation.required', 'This field is required'))
      .max(20, t('validation.maxLength', 'Maximum {{max}} characters', { max: 20 }));
  } else {
    // Optional address fields with partial address validation
    // If any address field is filled, all required address fields must be filled
    const addressFields = [fieldName('address'), fieldName('city'), fieldName('state'), fieldName('zipcode')];

    const hasAnyAddressField = function () {
      return addressFields.some(field => {
        const value = this.parent[field];
        return value && value.trim();
      });
    };

    schemaShape[fieldName('address')] = Yup.string()
      .max(500, t('validation.maxLength', 'Maximum {{max}} characters', { max: 500 }))
      .test(
        'partial-address-required',
        t('validation.partialAddressRequired', 'Complete address required when any address field is filled'),
        function (value) {
          if (!hasAnyAddressField.call(this)) return true; // No address fields filled, valid
          return !!(value && value.trim()); // If any filled, this one must be filled too
        },
      );

    schemaShape[fieldName('address_2')] = Yup.string().max(
      100,
      t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }),
    );

    schemaShape[fieldName('city')] = Yup.string()
      .max(100, t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }))
      .test(
        'partial-address-required',
        t('validation.partialAddressRequired', 'Complete address required when any address field is filled'),
        function (value) {
          if (!hasAnyAddressField.call(this)) return true;
          return !!(value && value.trim());
        },
      );

    schemaShape[fieldName('state')] = Yup.string()
      .max(100, t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }))
      .test(
        'partial-address-required',
        t('validation.partialAddressRequired', 'Complete address required when any address field is filled'),
        function (value) {
          if (!hasAnyAddressField.call(this)) return true;
          return !!(value && value.trim());
        },
      );

    schemaShape[fieldName('zipcode')] = Yup.string()
      .max(20, t('validation.maxLength', 'Maximum {{max}} characters', { max: 20 }))
      .test(
        'partial-address-required',
        t('validation.partialAddressRequired', 'Complete address required when any address field is filled'),
        function (value) {
          if (!hasAnyAddressField.call(this)) return true;
          return !!(value && value.trim());
        },
      );
  }

  schemaShape[fieldName('country')] = Yup.string().max(
    100,
    t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }),
  );

  // Add primary fields based on context
  const showRmaPrimary = primaryContext === 'all' || primaryContext === 'rma';
  const showSalesPrimary = primaryContext === 'all' || primaryContext === 'sales';

  if (showRmaPrimary) {
    schemaShape[fieldName('is_primary_rma_billing')] = Yup.boolean();
    schemaShape[fieldName('is_primary_rma_shipping')] = Yup.boolean();
  }

  if (showSalesPrimary) {
    schemaShape[fieldName('is_primary_sales_billing')] = Yup.boolean();
    schemaShape[fieldName('is_primary_sales_shipping')] = Yup.boolean();
  }

  return Yup.object().shape(schemaShape);
};

export default ContactFormFields;
